import { useCallback, useMemo } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "react-query";
import debounce from "lodash/debounce";
import moment from "moment/moment";
import { stringifyUrl } from "query-string";
import { useGlobalPrivateChannelListener } from "~/components/Hooks/useGlobalPrivateChannelListener";
import {
  INVOICE_STATUS_CANCELED,
  INVOICE_STATUS_PAID,
  INVOICE_STATUS_PARTIALLY_PAID,
  INVOICE_STATUS_SENT,
  INVOICE_STATUS_UNSENT,
} from "~/components/Invoices/utils";
import Angie from "~/utils/angie";
import { getDateBoundaries } from "~/utils/date";
import { fetch } from "~/utils/fetch";
import { param } from "~/utils/url_helper";

export const useInvoice = (id, config) => {
  return useQuery({
    ...config,
    queryKey: ["invoices", id],
    queryFn: () =>
      fetch({
        url: `${Angie.api_url}/invoices/${id}`,
      }),
  });
};

export const usePublicInvoice = (id, hash, config) => {
  return useQuery({
    ...config,
    queryKey: ["invoices", id],
    queryFn: () =>
      fetch({
        url: `${Angie.api_url}/s/invoice?number=${id}&hash=${hash}`,
      }),
  });
};

export const useInvoicesPrivateNotes = (config) => {
  return useQuery({
    ...config,
    queryKey: ["invoices", "private_notes"],
    queryFn: () =>
      fetch({
        url: `${Angie.api_url}/invoices/private-notes`,
      }),
  });
};

export const useUpdateInvoiceMutation = (id) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      fetch({
        url: `${Angie.api_url}/invoices/${id}`,
        method: "PUT",
        body: data,
      }),
    onSuccess: (response) => {
      queryClient.invalidateQueries(["invoices"]);
      queryClient.invalidateQueries(["invoices-available-filters"]);
      return response;
    },
  });
};

export const useCreateInvoiceMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      fetch({
        url: `${Angie.api_url}/invoices`,
        method: "POST",
        body: data,
      }),
    onSuccess: (response) => {
      queryClient.invalidateQueries(["invoices"]);
      queryClient.invalidateQueries(["invoices-available-filters"]);
      return response;
    },
  });
};

export const useInvoices = (params, enabled) => {
  const itemsPerPage = 30;

  if (enabled !== true && enabled !== false) {
    enabled = true;
  }

  const preparedParams = useMemo(() => {
    const result = {};

    if (params.dateRange?.from && params.dateRange?.to) {
      result["issued_on_from"] = params.dateRange?.from;
      result["issued_on_to"] = params.dateRange?.to;
    }

    if (params.filters?.statusFilter?.length) {
      result["status"] = params.filters?.statusFilter.map((item) => {
        return item.replace(/_/g, "");
      });
    }

    if (params.filters?.dueDateFilter) {
      const dateFormat = "YYYY-MM-DD";
      const { from: dueOnFrom, to: dueOnTo } = getDateBoundaries(params.filters?.dueDateFilter);

      result["due_on_from"] = dueOnFrom.format(dateFormat);
      result["due_on_to"] = dueOnTo.format(dateFormat);
    }

    if (params.filters?.currencyFilter?.length) {
      result["currency"] = params.filters?.currencyFilter;
    }

    const companyIds = [];
    const companyNames = [];

    if (params?.filters?.clientFilter?.length) {
      params?.filters?.clientFilter.forEach((client) => {
        if (Number.isInteger(client)) {
          companyIds.push(client);
        } else {
          companyNames.push(client);
        }
      });

      if (companyIds.length) result["company"] = companyIds;
      if (companyNames.length) result["company_name"] = companyNames;
    }

    if (params.sort?.field) {
      switch (params.sort.field) {
        case "issuedOn":
          result["sort"] = "issuedOn";
          break;
        case "dueOn":
          result["sort"] = "dueOn";
          break;
        case "activity":
          result["sort"] = "activity";
          break;
        case "amount":
          result["sort"] = "amount";
          break;
      }
    }

    if (params.sort?.disabledStatusSort) {
      result["disabled_status_sort"] = params.sort?.disabledStatusSort;
    }

    if (params.sort?.direction) {
      result["sort_direction"] = params.sort.direction;
    }

    return result;
  }, [params]);

  const queryFunction = useCallback(
    ({ pageParam = "last", signal }) => {
      const fetchParams = {
        cursor: pageParam,
        limit: itemsPerPage,
        ...preparedParams,
      };
      return fetch({
        url: `${Angie.api_url}/invoices/for-screen?${param(fetchParams)}`,
        signal,
        withHeaders: true,
      }).then((response) => {
        const counters = {};
        counters[INVOICE_STATUS_UNSENT] = response._headers["x-angie-invoicecountunsent"] || 0;
        counters[INVOICE_STATUS_SENT] = response._headers["x-angie-invoicecountsent"] || 0;
        counters[INVOICE_STATUS_PARTIALLY_PAID] = response._headers["x-angie-invoicecountpartially_paid"] || 0;
        counters[INVOICE_STATUS_PAID] = response._headers["x-angie-invoicecountpaid"] || 0;
        counters[INVOICE_STATUS_CANCELED] = response._headers["x-angie-invoicecountcanceled"] || 0;

        return {
          invoices: response._parsedBody,
          nextCursor: response._headers["x-angie-paginationnextcursor"],
          counters: counters,
        };
      });
    },
    [itemsPerPage, preparedParams]
  );

  return useInfiniteQuery({
    queryKey: ["invoices", "for-screen", preparedParams],
    queryFn: queryFunction,
    enabled: enabled,
    getNextPageParam: (lastPage) => {
      return lastPage?.nextCursor || undefined;
    },
  });
};

export const useInvoicesAvailableFilters = (dateRange, selectedFilters, enabled) => {
  const preparedParams = useMemo(() => {
    const result = {};

    if (dateRange?.from && dateRange?.to) {
      result["issued_on_from"] = dateRange?.from;
      result["issued_on_to"] = dateRange?.to;
    }

    if (selectedFilters?.statusFilter?.length) {
      result["include_status"] = selectedFilters.statusFilter.map((item) => {
        return item.replace(/_/g, "");
      });
    }

    if (selectedFilters?.clientFilter?.length) {
      const includeClientNames = [];
      const includeClientIds = [];

      selectedFilters.clientFilter.forEach((client) => {
        if (Number.isInteger(client)) {
          includeClientIds.push(client);
        } else {
          includeClientNames.push(client);
        }
      });

      if (includeClientIds.length) result["include_company"] = includeClientIds;
      if (includeClientNames.length) result["include_company_name"] = includeClientNames;
    }

    if (selectedFilters?.currencyFilter?.length) {
      result["include_currency"] = selectedFilters?.currencyFilter;
    }

    return result;
  }, [dateRange, selectedFilters]);

  return useQuery({
    queryKey: ["invoices-available-filters", preparedParams],
    queryFn: () =>
      fetch({
        url: stringifyUrl({
          url: `${Angie.api_url}/invoices/available-filters`,
          query: preparedParams,
        }),
      }),
    enabled: enabled,
  });
};

export const useInvoiceSuggestNumber = (config) => {
  return useQuery({
    ...config,
    queryKey: ["invoices", "suggest-number"],
    queryFn: ({ signal }) =>
      fetch({
        url: `${Angie.api_url}/invoices/suggest-number`,
        signal,
      }),
  });
};

export const useInvoiceDisableOverdueRemindersMutation = (id) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      fetch({
        url: `${Angie.api_url}/invoices/${id}`,
        method: "PUT",
        body: {
          is_muted: true,
        },
      }),
    onSuccess: (response) => {
      queryClient.invalidateQueries("invoices");
      queryClient.invalidateQueries("invoices-available-filters");
      return response;
    },
  });
};

export const useInvoiceEnableOverdueRemindersMutation = (id) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      fetch({
        url: `${Angie.api_url}/invoices/${id}`,
        method: "PUT",
        body: {
          is_muted: false,
        },
      }),
    onSuccess: (response) => {
      queryClient.invalidateQueries("invoices");
      queryClient.invalidateQueries("invoices-available-filters");
      return response;
    },
  });
};

export const useInvoiceMoveToTrashMutation = (id) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      fetch({
        url: `${Angie.api_url}/move-to-trash/invoice/${id}`,
        method: "PUT",
      }),
    onSuccess: (response) => {
      queryClient.invalidateQueries("invoices");
      queryClient.invalidateQueries("invoices-available-filters");
      return response;
    },
  });
};

export const useInvoiceCancelMutation = (id) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      fetch({
        url: `${Angie.api_url}/invoices/${id}/cancel`,
        method: "PUT",
      }),
    onSuccess: (response) => {
      queryClient.invalidateQueries("invoices");
      queryClient.invalidateQueries("invoices-available-filters");
      return response;
    },
  });
};

export const useInvoiceMarkAsSentMutation = (id) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      fetch({
        url: `${Angie.api_url}/invoices/${id}/mark-as-sent`,
        method: "POST",
      }),
    onSuccess: (response) => {
      queryClient.invalidateQueries("invoices");
      queryClient.invalidateQueries("invoices-available-filters");
      return response;
    },
  });
};

export const useInvoiceSendByEmail = (id) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      fetch({
        url: `${Angie.api_url}/invoices/${id}/send`,
        method: "PUT",
        body: data,
      }),
    onSuccess: (response) => {
      queryClient.invalidateQueries("invoices");
      queryClient.invalidateQueries("invoices-available-filters");
      return response;
    },
  });
};

export const useInvoiceMarkPayment = (id) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      fetch({
        url: `${Angie.api_url}/payments`,
        method: "POST",
        body: {
          ...data,
          ...{
            parent_id: id,
            parent_type: "Invoice",
          },
        },
      }),
    onSuccess: (response) => {
      queryClient.invalidateQueries("invoices");
      queryClient.invalidateQueries("invoices-available-filters");
      return response;
    },
  });
};

export const useInvoiceMarkAsPaid = (id) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      fetch({
        url: `${Angie.api_url}/invoices/${id}/mark-zero-invoice-as-paid`,
        method: "POST",
      }),
    onSuccess: (response) => {
      queryClient.invalidateQueries("invoices");
      queryClient.invalidateQueries("invoices-available-filters");
      return response;
    },
  });
};

export const useInvoiceDeletePayment = (id) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      fetch({
        url: `${Angie.api_url}/payments/${id}`,
        method: "DELETE",
      }),
    onSuccess: (response) => {
      queryClient.invalidateQueries("invoices");
      queryClient.invalidateQueries("invoices-available-filters");
      return response;
    },
  });
};

export const useInvoicesRealtime = () => {
  const queryClient = useQueryClient();
  const refetch = useCallback(() => {
    queryClient.invalidateQueries("invoices");
    queryClient.invalidateQueries("invoices-available-filters");
  }, [queryClient]);

  useGlobalPrivateChannelListener("InvoiceCreated", refetch);
  useGlobalPrivateChannelListener("InvoiceUpdated", refetch);
  useGlobalPrivateChannelListener("InvoiceMovedToTrash", refetch);
  useGlobalPrivateChannelListener("InvoiceRestoredFromTrash", refetch);
};

export const useInvoicesMetricsRealtime = () => {
  const queryClient = useQueryClient();
  const refetch = useMemo(() => {
    return debounce(() => {
      queryClient.invalidateQueries("company-currencies");
    }, 1000);
  }, [queryClient]);

  useGlobalPrivateChannelListener("InvoiceCreated", refetch);
  useGlobalPrivateChannelListener("InvoiceUpdated", refetch);
  useGlobalPrivateChannelListener("InvoiceMovedToTrash", refetch);
  useGlobalPrivateChannelListener("InvoiceRestoredFromTrash", refetch);
  useGlobalPrivateChannelListener("ProjectCreated", refetch);
  useGlobalPrivateChannelListener("ProjectMovedToTrash", refetch);
  useGlobalPrivateChannelListener("ProjectUpdated", refetch);
  useGlobalPrivateChannelListener("ProjectRestoredFromTrash", refetch);
  useGlobalPrivateChannelListener("TimeRecordCreated", refetch);
  useGlobalPrivateChannelListener("TimeRecordUpdated", refetch);
  useGlobalPrivateChannelListener("TimeRecordMovedToTrash", refetch);
  useGlobalPrivateChannelListener("TimeRecordRestoredFromTrash", refetch);
  useGlobalPrivateChannelListener("ExpenseCreated", refetch);
  useGlobalPrivateChannelListener("ExpenseUpdated", refetch);
  useGlobalPrivateChannelListener("ExpenseMovedToTrash", refetch);
  useGlobalPrivateChannelListener("ExpenseRestoredFromTrash", refetch);
  useGlobalPrivateChannelListener("TaskMovedToTrash", refetch);
  useGlobalPrivateChannelListener("TaskRestoredFromTrash", refetch);
};

export const useInvoiceItemsBuilderListProjects = (config) => {
  return useQuery({
    ...config,
    queryKey: ["invoices", "items-builder-list-projects"],
    queryFn: () =>
      fetch({
        url: `${Angie.api_url}/invoices/projects`,
      }),
  });
};

export const useInvoiceItemsBuilderBasedOnTrackingRecords = (queryParams) => {
  return useQuery({
    enabled: false,
    retry: 0,
    queryKey: ["invoices", "items-builder-based-on-tracking-records"],
    queryFn: () => {
      const fetchParams = {};

      if (queryParams?.includeTimeRecords === true) fetchParams.include_time_records = 1;
      if (queryParams?.includeExpenses === true) fetchParams.include_expenses = 1;
      if (queryParams?.projects?.length) fetchParams.project_ids = queryParams.projects;
      if (queryParams?.groupBy) fetchParams.sum_by = queryParams.groupBy;
      if (queryParams?.dateFilter?.from && queryParams?.dateFilter?.to) {
        fetchParams.date_filter_from = moment.unix(queryParams.dateFilter.from).utc().format("YYYY-MM-DD");
        fetchParams.date_filter_to = moment.unix(queryParams.dateFilter.to).utc().format("YYYY-MM-DD");
      }

      if (!fetchParams?.project_ids?.length || (!fetchParams.include_time_records && !fetchParams.include_expenses)) {
        return Promise.resolve([]);
      }

      return fetch({
        url: `${Angie.api_url}/invoices/preview-items?${param(fetchParams)}`,
      });
    },
  });
};

export const useInvoiceItemsBuilderBasedOnFixedBudgetProjects = (queryParams) => {
  return useQuery({
    enabled: false,
    retry: 0,
    queryKey: ["invoices", "items-builder-based-on-fixed-budget-projects"],
    queryFn: () => {
      let result = [];
      if (queryParams?.projects?.length) {
        let counter = 0;
        result = queryParams.data
          .filter((item) => queryParams.projects.includes(item.id))
          .map((item) => {
            return {
              id: ++counter,
              description: item?.name,
              quantity: 1,
              unit_cost: Number(item?.left_to_invoice),
              subtotal: Number(item?.left_to_invoice),
              project_id: Number(item?.id),
            };
          });
      }
      return Promise.resolve(result);
    },
  });
};

export const useQuickBooksInvoices = (params) => {
  const queryFunction = useCallback(
    ({ pageParam, signal }) => {
      return fetch({
        url: stringifyUrl(
          {
            url: `${Angie.api_url}/quickbooks/invoices/for-screen`,
            query: { cursor: pageParam, ...params },
          },
          { arrayFormat: "bracket" }
        ),
        signal,
        withHeaders: true,
      }).then((response) => {
        return {
          data: response._parsedBody,
          nextCursor: response._headers["x-angie-paginationnextcursor"],
        };
      });
    },
    [params]
  );

  return useInfiniteQuery({
    queryKey: ["quickbooks-invoices-for-screen"],
    queryFn: queryFunction,
    getNextPageParam: (lastPage) => {
      return lastPage?.nextCursor || undefined;
    },
  });
};

export const useCreateQuickBooksInvoiceMutation = () => {
  return useMutation({
    mutationFn: (data) =>
      fetch({
        url: `${Angie.api_url}/quickbooks/invoices`,
        method: "POST",
        body: data,
      }),
  });
};

export const useQuickBooksSync = (enabled, config) => {
  const queryClient = useQueryClient();
  return useQuery({
    enabled: enabled,
    ...config,
    queryKey: ["sync-quickbooks-invoices"],
    queryFn: () =>
      fetch({
        url: `${Angie.api_url}/quickbooks/invoices/sync`,
      }),
    onSuccess: (response) => {
      if (response?.updated?.length > 0 || response?.deleted?.length > 0) {
        queryClient.invalidateQueries("quickbooks-invoices-for-screen");
      }
      return response;
    },
  });
};

export const useQuickBooksInvoiceSuggestedNumber = (config) => {
  return useQuery({
    ...config,
    queryKey: ["quickbooks-invoices", "suggest-number"],
    queryFn: ({ signal }) =>
      fetch({
        url: `${Angie.api_url}/integrations/quickbooks/suggestion`,
        signal,
      }),
  });
};

export const useQuickBooksInvoiceCompanies = (config) => {
  return useQuery({
    ...config,
    queryKey: ["quickbooks-companies"],
    queryFn: ({ signal }) =>
      fetch({
        url: `${Angie.api_url}/integrations/quickbooks/data?entity=Customer`,
        signal,
      }),
  });
};

export const useQuickBooksItemTypes = (config) => {
  return useQuery({
    ...config,
    queryKey: ["quickbooks-item-types"],
    queryFn: ({ signal }) =>
      fetch({
        url: `${Angie.api_url}/integrations/quickbooks/item-types`,
        signal,
      }),
  });
};

export const useQuickBooksDefaultItemType = (config) => {
  return useQuery({
    ...config,
    queryKey: ["quickbooks-default-item-type"],
    queryFn: ({ signal }) =>
      fetch({
        url: `${Angie.api_url}/integrations/quickbooks/default-item-type`,
        signal,
      }),
  });
};
